// src/App.js
import React, { useState, createContext, useCallback, useEffect } from 'react';
import { Route, Routes, useNavigate, Navigate, Link, useLocation } from 'react-router-dom';
import { trackPageView, setAnalyticsUser } from './analytics';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from './firebase';
import HomePage from './components/HomePage';
import InvitePage from './components/InvitePage';
import AdminDashboard from './components/AdminDashboard';
import ProductPlanManagement from './components/ProductPlanManagement';
import ApiKeyIssuanceScreen from './components/apiKeyIssuanceScreen';
import NavBar from './components/NavBar';
import TeamSelector from './components/TeamSelector';
import authService from './services/authService';
import userService from './services/userService';
import CreateTeamPage from './components/CreateTeamPage'; // New component to be created
import AcceptInvite from './components/AcceptInvite'; // Import the new component
import ConsumptionDashboard from './components/ConsumptionDashboard'; // Import the new dashboard
import KeyDetail from './components/KeyDetail';
import LicenseActivationSigner from './components/LicenseActivationSigner';
import OfflineLicensingDashboard from './components/licensing/OfflineLicensingDashboard';
import SiteAdminDashboard from './components/SiteAdminDashboard';
import ProvisioningTokens from './components/ProvisioningTokens';
import CustomerOverview from './components/CustomerOverview';
import CustomerPortal from './components/portal/CustomerPortal';
import IssuanceLog from './components/IssuanceLog';

import './index.css';
import TailliDashboard from './dashboard';

export const AppContext = createContext(null);

function RouteTracker() {
  const location = useLocation();
  useEffect(() => {
    trackPageView({ path: location.pathname + location.search });
  }, [location.pathname, location.search]);
  return null;
}

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState(null);
  const [userTeams, setUserTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [needsTeam, setNeedsTeam] = useState(false); // New state for users without teams
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const navigate = useNavigate();

  const handleTeamChange = useCallback((team) => {
    console.log('Team changed:', team);
    setSelectedTeam(team);

    // It is possible that team is not defined, so we need to check for that
    // - Set role if we have one
    // - If not, set role to '-'
    if (team && team.role) {
      setUserRole(team.role);
    } else {
      setUserRole('-');
    }

    localStorage.setItem('selectedTeam', JSON.stringify(team));
  }, []);

  const handleLogout = useCallback(async () => {
    await authService.logout();
    setIsLoggedIn(false);
    setUser(null);
    setUserTeams([]);
    setSelectedTeam(null);
    setUserRole(null);
    setNeedsTeam(false);
    setIsSuperAdmin(false);
    localStorage.removeItem('selectedTeam');
    navigate('/');
  }, [navigate]);

  // Memoize fetchUserData to ensure stable reference
  const fetchUserData = useCallback(async () => {
    try {
      setLoading(true);
      const token = authService.getToken();
      if (!token) {
        setIsLoggedIn(false);
        setLoading(false);
        setUser(null); // Clear user state if no token
        setUserTeams([]);
        setSelectedTeam(null);
        setUserRole(null);
        setNeedsTeam(false);
        setIsSuperAdmin(false);
        return;
      }

      // Fetch user details using the new endpoint
      const userDetails = await userService.getUserDetails(token);
      console.log('User details received:', userDetails);
      setUser(userDetails);
      setIsSuperAdmin(!!userDetails.isSuperAdmin);

      let teams = [];
      try {
        teams = await userService.getUserTeams();
      } catch (teamError) {
        console.error('Failed to fetch team list for user:', teamError);
      }

      if ((!teams || teams.length === 0) && Array.isArray(userDetails.teams)) {
        teams = userDetails.teams.map((team) => ({
          teamId: team.teamId || team.team_id,
          teamName: team.teamName || team.team_name,
          role: team.role,
          isCustomerProxy: !!team.isCustomerProxy,
          vendorTeamId: team.vendorTeamId || null
        })).filter(team => team.teamId);
      }

      // Fallback to the single-team format if the dedicated endpoint failed
      if ((!teams || teams.length === 0) && userDetails.user && userDetails.user.team_id) {
        teams = [{
          teamId: userDetails.user.team_id,
          teamName: userDetails.user.team_name,
          role: userDetails.user.role
        }];
      }

      if (Array.isArray(teams) && teams.length > 0) {
        console.log('User teams loaded:', teams.map(t => t.teamId));
        setNeedsTeam(false);
        setUserTeams(teams);

        const storedSelection = localStorage.getItem('selectedTeam');
        let preferredTeam = null;

        if (storedSelection) {
          try {
            const parsedSelection = JSON.parse(storedSelection);
            preferredTeam = teams.find(team => team.teamId === parsedSelection.teamId) || null;
          } catch (parseError) {
            console.warn('Failed to parse stored team selection:', parseError);
          }
        }

        const teamToUse = preferredTeam || teams[0];
        setSelectedTeam(teamToUse);
        setUserRole(teamToUse?.role || '-');
        localStorage.setItem('selectedTeam', JSON.stringify(teamToUse));
      } else {
        console.log('User has no teams');
        setNeedsTeam(true);
        setUserTeams([]);
        setSelectedTeam(null);
        setUserRole(null);
      }
      
      setIsLoggedIn(true);
    } catch (error) {
      console.error('Failed to fetch user data:', error);
      // If fetching fails (e.g., invalid token), log out the user
      await handleLogout(); 
    } finally {
      setLoading(false);
    }
  }, [handleLogout]); // Add handleLogout as dependency

  useEffect(() => {
    fetchUserData(); // Initial fetch on component mount
  }, [fetchUserData]); // Run only when fetchUserData changes (which should be stable due to useCallback)

  useEffect(() => {
    // GA4 user_id must not contain PII — use the internal id, not email
    setAnalyticsUser(user?.id || user?.user?.id || null);
  }, [user]);

  // A customer-org user is a member of a customer proxy team (flagged server-side).
  // Drives the portal-vs-vendor split for routing and nav.
  const isCustomer = !!selectedTeam?.isCustomerProxy;

  // Context value includes state and the refresh function
  const contextValue = {
    isLoggedIn,
    user,
    userTeams,
    selectedTeam,
    userRole,
    loading,
    needsTeam,
    isSuperAdmin,
    fetchUserData, // Expose the function
    handleTeamChange,
    handleLogout
  };

  // Show initial loading only before we know auth state (no header/nav yet)
  if (loading && !user) {
    return (
      <div className="min-h-screen bg-slate-900 text-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-base text-slate-400">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <AppContext.Provider value={contextValue}>
      <RouteTracker />
      <div className="min-h-screen bg-slate-900 text-slate-100">
        {/* Beta corner ribbon - only on landing page */}
        {!isLoggedIn && (
          <div className="fixed top-0 right-0 z-50 overflow-hidden pointer-events-none" aria-hidden="true" style={{ width: '150px', height: '150px' }}>
            <div
              className="absolute bg-indigo-600 text-white text-[11px] font-bold tracking-[0.2em] uppercase text-center py-1.5"
              style={{ top: '26px', right: '-35px', width: '170px', transform: 'rotate(45deg)', boxShadow: '0 2px 4px rgba(0,0,0,0.3)' }}
            >
              BETA
            </div>
          </div>
        )}
        <header className="border-b border-slate-800 bg-slate-950">
          <div className="px-6 py-3 flex justify-between items-center">
            <a href="/" className="flex items-center space-x-2 hover:opacity-80 transition-opacity">
              <img src="/tailli_logo.png" alt="Táillí Logo" className="h-8" />
              <span className="text-lg font-semibold text-slate-100">Táillí</span>
            </a>
            {isLoggedIn && (
              <div className="flex items-center gap-6">
                {!needsTeam && userTeams.length > 0 && (
                  <TeamSelector
                    teams={userTeams}
                    selectedTeam={selectedTeam}
                    onTeamChange={handleTeamChange}
                  />
                )}
                {isSuperAdmin && (
                  <Link
                    to="/site-admin"
                    className="text-sm text-indigo-400 hover:text-indigo-300 transition-colors"
                  >
                    Site Admin
                  </Link>
                )}
                <a
                  href="/docs/"
                  className="text-sm text-indigo-400 hover:text-indigo-300 transition-colors"
                >
                  Docs
                </a>
                <span className="text-sm text-slate-500">
                  {user?.email}
                </span>
                <button
                  onClick={handleLogout}
                  className="text-sm text-slate-400 hover:text-slate-200 transition-colors"
                >
                  Logout
                </button>
              </div>
            )}
          </div>
        </header>

        {isLoggedIn && (
          <NavBar onLogout={handleLogout} userEmail={user?.email} selectedTeamName={selectedTeam?.teamName} userRole={userRole} isCustomer={isCustomer} />
        )}

        <main className="max-w-7xl mx-auto px-6 py-8">
          <Routes>
            <Route path="/" element={isLoggedIn ? (needsTeam ? <Navigate to="/create-team" /> : <Navigate to={isCustomer ? '/portal/keys' : '/consumption'} />) : <HomePage onLoginSuccess={fetchUserData} />} />
            <Route path="/create-team" element={isLoggedIn ? <CreateTeamPage /> : <Navigate to="/" />} />
            <Route path="/invite/:token" element={<AcceptInvite />} />
            {/* Customer self-service portal (members of a customer proxy team). Vendors are bounced to their dashboard. */}
            <Route path="/portal/keys" element={isLoggedIn ? (needsTeam ? <Navigate to="/create-team" /> : (isCustomer ? <CustomerPortal /> : <Navigate to="/consumption" />)) : <Navigate to="/" />} />
            {/* Vendor admin surfaces — not available to customer-org users. */}
            {!isCustomer && (
              <>
                <Route path="/dashboard" element={isLoggedIn ? (needsTeam ? <Navigate to="/create-team" /> : <TailliDashboard />) : <Navigate to="/" />} />
                <Route path="/admin" element={isLoggedIn ? (needsTeam ? <Navigate to="/create-team" /> : <AdminDashboard user={user} selectedTeam={selectedTeam} currentUserId={user?.id} />) : <Navigate to="/" />} />
                <Route path="/products" element={isLoggedIn ? (needsTeam ? <Navigate to="/create-team" /> : <ProductPlanManagement />) : <Navigate to="/" />} />
                <Route path="/apikeys" element={isLoggedIn ? (needsTeam ? <Navigate to="/create-team" /> : <ApiKeyIssuanceScreen />) : <Navigate to="/" />} />
                <Route path="/manage-keys" element={isLoggedIn ? (needsTeam ? <Navigate to="/create-team" /> : <ApiKeyIssuanceScreen />) : <Navigate to="/" />} />
                <Route path="/keys/:apiKeyId" element={isLoggedIn ? (needsTeam ? <Navigate to="/create-team" /> : <KeyDetail />) : <Navigate to="/" />} />
                <Route path="/consumption" element={isLoggedIn ? (needsTeam ? <Navigate to="/create-team" /> : <ConsumptionDashboard />) : <Navigate to="/" />} />
                <Route path="/customer-overview" element={isLoggedIn ? (needsTeam ? <Navigate to="/create-team" /> : <CustomerOverview />) : <Navigate to="/" />} />
                <Route path="/issuance-log" element={isLoggedIn ? (needsTeam ? <Navigate to="/create-team" /> : <IssuanceLog />) : <Navigate to="/" />} />
                <Route path="/admin/license-activate" element={isLoggedIn ? (needsTeam ? <Navigate to="/create-team" /> : <LicenseActivationSigner />) : <Navigate to="/" />} />
                <Route path="/licensing" element={isLoggedIn ? (needsTeam ? <Navigate to="/create-team" /> : <OfflineLicensingDashboard />) : <Navigate to="/" />} />
                <Route path="/provisioning-tokens" element={isLoggedIn ? (needsTeam ? <Navigate to="/create-team" /> : <ProvisioningTokens />) : <Navigate to="/" />} />
                <Route path="/site-admin" element={isLoggedIn && isSuperAdmin ? <SiteAdminDashboard /> : <Navigate to="/" />} />
              </>
            )}
            {/* A customer-org user hitting any vendor path lands back in their portal. */}
            {isCustomer && <Route path="*" element={<Navigate to="/portal/keys" />} />}
          </Routes>
        </main>

        <footer className="border-t border-slate-800 py-6 text-center text-sm text-slate-500">
          © 2026 Latent Search Limited
        </footer>
      </div>
    </AppContext.Provider>
  );
}

export default App;
